import "../../../scss/pages/admin/_admin-clients.scss";

import {
  DatePicker,
  DialogActionButton,
  TextField,
  Toggle,
} from "@finpay-development/shared-components";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { MenuItem, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import moment from "moment";

import AddressValidator from "../../../shared/components/address-validator";
import {
  stateAbbrevLookup,
  stateAbbrevs,
} from "@finpay-development/shared-platform/lib/esm/shared/misc/us-states";
import { ClientStatusCardViewModel } from "@finpay-development/shared-platform/lib/esm/shared/model/client-status-card";
import { PrimaryAddress } from "@finpay-development/shared-platform/lib/esm/shared/model/primary-address";
import {
  ContactIncome,
  PhoneType,
  PrimaryContact,
} from "@finpay-development/shared-platform/lib/esm/shared/model/primary-contact";
import { State } from "@finpay-development/shared-platform/lib/esm/shared/model/state";
import { RootState } from "../../../shared/state/root-reducer";
import { AppDispatch } from "../../../shared/state/store";
import { Utils } from "@finpay-development/shared-platform/lib/esm/shared/utils";
import { patientInfo } from "@finpay-development/shared-platform/lib/esm/shared/validation/schemas";
import { savePatient } from "@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk";
import { PatientViewModel } from "@finpay-development/shared-platform/lib/esm/patient/components/models/patient-view-model";
import {userService} from '@finpay-development/shared-platform/lib/esm/admin/services/admin-user-service';
import {showErrorStatus} from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';

interface PatientModalProps {
  open: boolean;
  isEdit: boolean;
  handleModalCancel: () => void;
  handleModalSubmit: () => void;
}

export function PatientModal(props: PatientModalProps) {
  const selectors = {
    clients: useSelector(
      (state: RootState) =>
        state.implementationContext.implementationSpecialistClient
          .filteredByIsActive
    ),
    selectedPatient: useSelector(
      (state: RootState) => state.patientContext.selectedPatient
    ),
    modalSaveStatus: useSelector(
      (state: RootState) => state.patientContext.savePatientModalSaveStatus
    ),
    errorMessage: useSelector(
      (state: RootState) => state.patientContext.errorMessage
    ),
    shouldReFetchClients: useSelector((state: RootState) => {
      return !(
        state.implementationContext?.implementationSpecialistClient?.allClients
          ?.length > 0
      );
    }),
  };

  const { clients, modalSaveStatus, selectedPatient } = selectors;
  const { open, isEdit, handleModalCancel, handleModalSubmit } = props;

  const addressValidator = new AddressValidator();

  const [enableSaveButton, setEnableSaveButton] = useState(false);

  const formRef: any = useRef();
  const dispatch = useDispatch<AppDispatch>();

  const mapToSaveModel = () => {
    const patient = selectors.selectedPatient;
    const formValues = formRef.current.values;
    const stateInfo = stateAbbrevLookup(formValues.state);
    const state = {
      stateId: stateInfo.id,
      stateCode: formValues.state,
      stateName: stateInfo.name,
    } as State;

    const address: PrimaryAddress = {
      streetAddress1: formValues.streetAddress1,
      streetAddress2: formValues.streetAddress2,
      city: formValues.city,
      state: state,
      zipCode: formValues.zipCode,
      zipCode4: formValues.zipCode4Plus,
      addressType: "Primary",
    };

    const contactIncome = {
      yearlySalary: formValues.salary,
      yearsAtEmployer: formValues.yearsAtEmployer,
    } as ContactIncome;
    const birthday = formValues.dateOfBirth;
    const formattedBirthday = Utils.convertDate(new Date(birthday));
    const contact = {
      title: formValues.title,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      middleName: formValues.middleName,
      primaryAddress: address,
      email: formValues.email,
      externalId: isEdit ? patient?.contact?.externalId : undefined,
      primPhoneNum: formValues.firstPhone,
      primPhoneType: formValues.isFirstPhoneMobile
        ? PhoneType.mobile
        : PhoneType.home,
      secPhoneNum: formValues.secondPhone,
      secPhoneType: formValues.isSecondPhoneMobile
        ? PhoneType.mobile
        : PhoneType.home,
      leaveVMFl: formValues.leaveVoicemail,
      smsConsentFl: formValues.receiveSMSMessages,
      ssn: !formValues.socialSecurityNumber?.includes("●")
        ? formValues.socialSecurityNumber
        : "",
      ssnLast4: !formValues.socialSecurityNumber?.includes("●")
        ? formValues.socialSecurityNumber.slice(-4)
        : "",
      birthDate: formattedBirthday,
      contactIncome: contactIncome,
    } as PrimaryContact;

    const patientToSave = {
        contact: contact,
        patientId: formValues?.patientId,
        clientId: formValues.clientId,
    } as PatientViewModel;

    if(isEdit) {
      delete patientToSave.clientId
    }

    return patientToSave
  };

  async function handleSave() {

    await getZipCodePlus4();
    const patientToSave = mapToSaveModel();

    try{
      const response = await userService.handleCreateOrUpdateUser({
        userEmail: patientToSave.contact.email,
        clientId: isEdit ? +selectedPatient.clientId! : +patientToSave.clientId!,
        name: {
          firstName: patientToSave.contact.firstName,
          lastName: patientToSave.contact.lastName
        }
      })
      patientToSave.userId = response.userId;
    }catch(e: any){
      dispatch(showErrorStatus(e.message))
      return
    }

    await dispatch(savePatient({
      patient: patientToSave
    }));

    handleModalSubmit();

  }

  function handleCancelCallback() {
    handleModalCancel();
  }

  const patientContact = selectors.selectedPatient.contact;

  const initialValuesEdit = {
    clientId: selectors.selectedPatient.clientId,
    patientId: selectors.selectedPatient.patientId,
    title: `${patientContact?.title ? patientContact.title : ""}`,
    firstName: patientContact.firstName,
    middleName: `${
      patientContact?.middleName ? patientContact.middleName : ""
    }`,
    lastName: patientContact.lastName,
    email: patientContact.email,
    retypeEmail: patientContact.email,
    firstPhone: patientContact.primPhoneNum,
    isFirstPhoneMobile: patientContact.primPhoneType === PhoneType.mobile,
    secondPhone: patientContact.secPhoneNum,
    isSecondPhoneMobile: patientContact.secPhoneType === PhoneType.mobile,
    leaveVoicemail: patientContact.leaveVMFl,
    receiveSMSMessages: patientContact.smsConsentFl,
    streetAddress1: patientContact?.primaryAddress?.streetAddress1,
    streetAddress2: `${
      patientContact?.primaryAddress?.streetAddress2
        ? patientContact.primaryAddress.streetAddress2
        : ""
    }`,
    city: patientContact.primaryAddress?.city,
    state: patientContact.primaryAddress?.state?.stateCode,
    zipCode: patientContact.primaryAddress?.zipCode,
    dateOfBirth: patientContact.birthDate,
    socialSecurityNumber: `${
      patientContact.ssnLast4 ? "●●●-●●-" + patientContact.ssnLast4 : ""
    }`,
    salary: patientContact.contactIncome?.yearlySalary || "",
    yearsAtEmployer: patientContact.contactIncome?.yearsAtEmployer || "",
  };

  const initialValuesAdd = {
    clientId: null,
    patientId: 0,
    title: "-1",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    retypeEmail: "",
    firstPhone: "",
    isFirstPhoneMobile: true,
    secondPhone: "",
    isSecondPhoneMobile: false,
    leaveVoicemail: false,
    receiveSMSMessages: true,
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "-1",
    zipCode: "",
    dateOfBirth: null,
    socialSecurityNumber: "",
    salary: "",
    yearsAtEmployer: "",
  };

  const validationSchema = Yup.object({
    ...patientInfo,
    salary: Yup.string().matches(/^\d+$/, {
      message: "Salary must be a positive number",
      excludeEmptyString: true,
    }),
  });

  const titles = ["Mr.", "Mrs.", "Ms.", "Dr."];

  async function getZipCodePlus4() {
    formRef.current.values.zipCode4Plus =
      await addressValidator.getZipCodePlus4(formRef.current.values);
  }

  function checkIfIsValid(value: any) {
    validationSchema
      .validate(value)
      .then(() => {
        if (Utils.isValidDate(value?.dateOfBirth)) {
          setEnableSaveButton(true);
        }
      })
      .catch(() => {
        setEnableSaveButton(false);
      });
  }

  return (
    <Dialog
      scroll="body"
      className="modal client-modal"
      open={open}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle>
        <span className="title">
          {`${isEdit ? "Edit" : "Add"}`} Patient Record
        </span>
      </DialogTitle>
      <DialogContent>
        <Formik
          innerRef={formRef}
          initialValues={isEdit ? initialValuesEdit : initialValuesAdd}
          validationSchema={validationSchema}
          validate={checkIfIsValid}
          onSubmit={() => {}}
        >
          {(formik) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    Treatment Provider
                  </Typography>
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    select={true}
                    error={
                      formik.touched["clientId"] && formik.errors["clientId"]
                    }
                    disabled={isEdit}
                    label="Client"
                    name="clientId"
                    value={formik.values.clientId}
                    placeholder="Select Client"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    test-id="patient-modal-client"
                  >
                    <MenuItem value="-1">Select</MenuItem>
                    {clients.map((client: ClientStatusCardViewModel) => (
                      <MenuItem key={client.clientId} value={client.clientId}>
                        {client.clientName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Name</Typography>
                </Grid>
                <Grid xs={3} item>
                  <TextField
                    select={true}
                    error={formik.touched["title"] && formik.errors["title"]}
                    label="Title"
                    name="title"
                    value={formik.values.title}
                    placeholder="Select"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required={false}
                  >
                    <MenuItem value="-1">Select</MenuItem>
                    {titles.map((title: string) => (
                      <MenuItem key={title} value={title}>
                        {title}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid xs={3} item>
                  <TextField
                    error={
                      formik.touched["firstName"] && formik.errors["firstName"]
                    }
                    label="First Name"
                    placeholder="Enter First Name"
                    name="firstName"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    test-id="patient-modal-first-name"
                  />
                </Grid>
                <Grid xs={2} item>
                  <TextField
                    error={
                      formik.touched["middleName"] &&
                      formik.errors["middleName"]
                    }
                    label="M.I."
                    maxLength={2}
                    name="middleName"
                    value={formik.values.middleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required={false}
                  />
                </Grid>
                <Grid xs={4} item>
                  <TextField
                    error={
                      formik.touched["lastName"] && formik.errors["lastName"]
                    }
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter Last Name"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    test-id="patient-modal-last-name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    Contact Information
                  </Typography>
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    error={formik.touched["email"] && formik.errors["email"]}
                    label="Email"
                    name="email"
                    placeholder="Enter Email Address"
                    value={formik.values.email}
                    maxLength={128}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    test-id="patient-modal-email"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    error={
                      formik.touched["retypeEmail"] &&
                      formik.errors["retypeEmail"]
                    }
                    label="Retype Email"
                    name="retypeEmail"
                    placeholder="Re-Enter Email Address"
                    value={formik.values.retypeEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    test-id="patient-modal-retype-email"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    error={
                      formik.touched["firstPhone"] &&
                      formik.errors["firstPhone"]
                    }
                    label="Phone"
                    maxLength={12}
                    name="firstPhone"
                    placeholder="Enter Phone Number"
                    value={formik.values.firstPhone}
                    onChange={(e: Event) => {
                      formik.handleChange(e);
                      formik.setFieldValue(
                        "firstPhone",
                        Utils.formatPhoneNumber(
                          (e.target as HTMLInputElement).value
                        )
                      );
                    }}
                    onBlur={formik.handleBlur}
                    test-id="patient-modal-first-phone"
                  />
                </Grid>
                <Grid xs={6} item>
                  <Toggle
                    leftText="Mobile"
                    rightText="Home"
                    name="isFirstPhoneMobile"
                    value={formik.values.isFirstPhoneMobile}
                    formik={formik}
                    test-id="patient-modal-first-phone-type-toggle"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    error={
                      formik.touched["secondPhone"] &&
                      formik.errors["secondPhone"]
                    }
                    label="Phone"
                    name="secondPhone"
                    placeholder="Enter Phone Number"
                    maxLength={12}
                    value={formik.values.secondPhone}
                    onChange={(e: Event) => {
                      formik.handleChange(e);
                      formik.setFieldValue(
                        "secondPhone",
                        Utils.formatPhoneNumber(
                          (e.target as HTMLInputElement).value
                        )
                      );
                    }}
                    onBlur={formik.handleBlur}
                    required={false}
                    test-id="patient-modal-second-phone"
                  />
                </Grid>
                <Grid xs={6} item>
                  <Toggle
                    leftText="Mobile"
                    rightText="Home"
                    name="isSecondPhoneMobile"
                    value={formik.values.isSecondPhoneMobile}
                    formik={formik}
                  />
                </Grid>
                <Grid xs={3} item style={{ marginBottom: -12 }}>
                  <Typography variant="h4">Leave Voicemail</Typography>
                </Grid>
                <Grid xs={9} item style={{ marginBottom: -12 }}>
                  <Typography variant="h4">Receive SMS Messages</Typography>
                </Grid>
                <Grid xs={3} item>
                  <Toggle
                    name="leaveVoicemail"
                    value={formik.values.leaveVoicemail}
                    formik={formik}
                  />
                </Grid>
                <Grid xs={9} item>
                  <Toggle
                    name="receiveSMSMessages"
                    value={formik.values.receiveSMSMessages}
                    formik={formik}
                    test-id="patient-modal-receive-sms-toggle"
                  />
                </Grid>
                <Grid xs={12} item>
                  <Typography variant="subtitle2">Address</Typography>
                </Grid>
                <Grid xs={12} item>
                  <TextField
                    error={
                      formik.touched["streetAddress1"] &&
                      formik.errors["streetAddress1"]
                    }
                    label="Street Address 1"
                    name="streetAddress1"
                    minLength={3}
                    placeholder="Enter Street Address"
                    value={formik.values.streetAddress1}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    test-id="patient-modal-street-address-1"
                  />
                </Grid>
                <Grid xs={12} item>
                  <TextField
                    error={
                      formik.touched["streetAddress2"] &&
                      formik.errors["streetAddress2"]
                    }
                    label="Street Address 2"
                    name="streetAddress2"
                    minLength={3}
                    value={formik.values.streetAddress2}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required={false}
                    test-id="patient-modal-street-address-2"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    error={formik.touched["city"] && formik.errors["city"]}
                    label="City"
                    name="city"
                    placeholder="Enter City"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    test-id="patient-modal-city"
                  />
                </Grid>
                <Grid xs={3} item>
                  <TextField
                    select={true}
                    error={formik.touched["state"] && formik.errors["state"]}
                    label="State"
                    name="state"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="state-field"
                    test-id="patient-modal-state"
                  >
                    <MenuItem value="-1">Select</MenuItem>
                    {stateAbbrevs.map((state: string) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid xs={3} item>
                  <TextField
                    error={
                      formik.touched["zipCode"] && formik.errors["zipCode"]
                    }
                    label="Zip Code"
                    minLength={5}
                    maxLength={5}
                    name="zipCode"
                    placeholder="Enter Zip Code"
                    value={formik.values.zipCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    test-id="patient-modal-zip-code"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    Personal Information
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    data="date-input"
                    error={
                      Utils.isValidDate(formik.values.dateOfBirth) &&
                      formik.touched["dateOfBirth"] &&
                      formik.errors["dateOfBirth"]
                    }
                    label="Date of Birth"
                    value={formik.values.dateOfBirth}
                    onChange={(date: Date) => {
                      if (!Utils.isFutureDate(date))
                        formik.setFieldValue("dateOfBirth", date);
                      else formik.setFieldValue("dateOfBirth", moment());
                    }}
                    minDate={Utils.getMinDate()}
                    maxDate={moment()}
                    test-id="patient-modal-date-of-birth"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={
                      formik.touched["socialSecurityNumber"] &&
                      formik.errors["socialSecurityNumber"]
                    }
                    label="Social Security Number"
                    maxLength={11}
                    name="socialSecurityNumber"
                    placeholder="Enter Social Security Number"
                    value={formik.values.socialSecurityNumber}
                    onChange={(e: Event) => {
                      formik.handleChange(e);
                      formik.setFieldValue(
                        "socialSecurityNumber",
                        Utils.formatSSN((e.target as HTMLInputElement).value)
                      );
                    }}
                    onBlur={formik.handleBlur}
                    required={false}
                    test-id="patient-modal-social-security-number"
                  />
                </Grid>
                <Grid xs={12} item>
                  <Typography variant="subtitle2">
                    Employment Information
                  </Typography>
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    error={
                      formik.touched["salary"] && formik.errors["salary"]
                    }
                    type="number"
                    label="Salary"
                    placeholder="Enter Salary"
                    startAdornment={<AttachMoneyIcon />}
                    name="salary"
                    value={formik.values.salary}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required={false}
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    error={
                      formik.touched["yearsAtEmployer"] &&
                      formik.errors["yearsAtEmployer"]
                    }
                    type="number"
                    label="Years At Employer"
                    name="yearsAtEmployer"
                    placeholder="Enter Years at Employer"
                    value={formik.values.yearsAtEmployer}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required={false}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <DialogActionButton
          isEnabled={enableSaveButton}
          savebuttonText="Save"
          saveStatus={modalSaveStatus}
          executeSave={handleSave}
          handleCallbackSave={() => {}} // handled in thunk
          handleCallbackCancel={handleCancelCallback}
          test-id="patient-modal-save-button"
        />
      </DialogActions>
    </Dialog>
  );
}
